import React, { useState, useEffect } from 'react';

/**
 * A toast notification component
 * 
 * @param {Object} props - Component props
 * @param {string} props.message - The message to display
 * @param {string} props.type - The type of toast (success, error, info)
 * @param {number} props.duration - How long to show the toast in milliseconds
 * @param {Function} props.onClose - Function to call when the toast is closed
 * @param {boolean} props.silent - If true, the toast won't trigger page reloads
 * @returns {JSX.Element|null} - The toast component or null if no message
 */
const Toast = ({ 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose,
  silent = false
}) => {
  const [visible, setVisible] = useState(true);

  // Ensure a new incoming message re-opens the toast if it was previously closed
  useEffect(() => {
    if (message) {
      setVisible(true);
    }
  }, [message]);
  
  useEffect(() => {
    if (!message) return;
    
    // If silent mode is enabled, just show the toast without side effects
    if (silent) {
      console.log('Silent toast notification:', message);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          if (onClose) onClose();
        }, 300); // Wait for fade out animation
      }, duration);
      
      return () => clearTimeout(timer);
    }
    
    // Regular toast behavior
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        if (onClose) onClose();
      }, 300); // Wait for fade out animation
    }, duration);
    
    return () => clearTimeout(timer);
  }, [message, duration, onClose, silent]);
  
  if (!message || !visible) return null;
  
  return (
    <div className={`toast-container ${visible ? 'visible' : ''}`}>
      <div className={`toast ${type}`}>
        <div className="toast-message">{message}</div>
        <button 
          className="toast-close"
          onClick={() => {
            setVisible(false);
            setTimeout(() => {
              if (onClose) onClose();
            }, 300); // Wait for fade out animation
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default Toast;